import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { AccountType } from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { useAppSelector } from '@core/store';
import { useLazyGetAllBranchesQuery } from '@features/branch-management/queries';
import useSwitchToDefaultBranch from '@features/settings/components/branch-visibility/useSwitchToDefaultBranch';
import useCheckPermission from '@hooks/checkPermission';
import { Branch } from '@types';
import { getLocalStorageItem, setLocalStorageItem } from '@utils/storage';

import BranchSwitcher from './BranchSwitcher';

interface BranchSelectionProps {
  onBranchChange: (branchId: string, branchName: string) => void;
  initialBranchName: string;
  handleAddNewBranch: (branchCount: number) => void;
}

const BranchSelection = ({
  onBranchChange,
  initialBranchName,
  handleAddNewBranch
}: BranchSelectionProps) => {
  const { currentOrg } = useSelector((state: any) => state.user);
  const noBranchAccess = useAppSelector((state) => state.user.noBranchAccess);
  const { currentBranchFilter } = useSelector((state: any) => state.branch);
  const isTrialMode = useSelector((state: any) => state.trial?.isTrialMode);
  const { switchToDefaultBranch } = useSwitchToDefaultBranch();

  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const branchName = initialBranchName || currentOrg?.default_branch_name;

  const [branchesList, setBranchesList] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>(branchName);
  const { checkPermission } = useCheckPermission();

  const hasCreateBranchAccess = checkPermission([
    UserPermissionTypes.BRANCH_CREATE
  ]);

  const [
    getAllBranches,
    { isLoading: isLoadingBranches, isFetching: isFetchingBranches }
  ] = useLazyGetAllBranchesQuery();

  // The list only holds visible branches, so a missing active branch means it
  // was hidden or archived (or none was ever resolved) — move the user off it.
  const leaveUnlistedBranch = (branches: Branch[]) => {
    const activeBranchId =
      currentBranchFilter?.branchId ||
      getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID);
    if (
      isTrialMode ||
      noBranchAccess ||
      branches.some(({ id }) => id === activeBranchId)
    )
      return;
    switchToDefaultBranch(organizationId);
  };

  const handleGetAllBranches = async () => {
    const res = await getAllBranches({
      organizationId
    });
    if (res?.data) {
      setBranchesList(res.data?.data);
      leaveUnlistedBranch(res.data.data);
      // No visible branch left: don't keep showing the stored (stale) name.
      if (!res.data.data.length) setSelectedBranch('');
      if (res.data.data.length === 1) {
        setSelectedBranch(res.data.data[0].name);
        setLocalStorageItem(
          LOCAL_STORAGE_KEYS.BRANCH_NAME,
          res.data.data[0].name
        );
      }
    }
  };

  const handleBranchChange = (branch: string) => {
    setSelectedBranch(branch);
    const selectedBranchId = branchesList?.find(
      (b: Branch) => b.name === branch
    )?.id;
    if (selectedBranchId) onBranchChange(selectedBranchId, branch);
  };

  useEffect(() => {
    if (
      currentOrg?.id === organizationId &&
      currentOrg?.type === AccountType.ORGANIZATION
    ) {
      handleGetAllBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizationId,
    currentOrg?.id,
    currentBranchFilter?.branchName,
    currentBranchFilter?.imageUrl
  ]);

  useEffect(() => {
    if (branchName) setSelectedBranch(branchName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchName]);

  return (
    <BranchSwitcher
      selectedBranch={selectedBranch}
      branchesList={branchesList}
      handleAddNewBranch={handleAddNewBranch}
      hasCreateBranchAccess={hasCreateBranchAccess}
      onBranchChange={handleBranchChange}
      isLoading={isLoadingBranches || isFetchingBranches}
    />
  );
};

export default BranchSelection;
